
export { AjaxFormValidation };

/**
 * Ajax form validation for both server side and client side
 *
 * Supports bootstrap 4/5 validation errors/elements 
 */
class AjaxFormValidation {
    
    /**
     * @param {jQuery|object} form
     * @param {string} url
     * @param {string} validation The validation associated to the cakephp model
     * @param {object} options jquery-validation validate options
     * @param {function} insertValidation custom validation message insert function, default is element.after(validation)
     * @link https://jqueryvalidation.org/validate/
     */
    constructor(form, url, validation, options = {}, insertValidation = null) {
        this.form = form;
        this.url = url;
        this.validation = validation;
        insertValidation = insertValidation || function(element, validation){
            element.after(validation);
        };
        this.insertValidation = insertValidation;
        this.validator = this.form.validate($.extend({
            errorElement: 'div',
            errorPlacement(error, element) {
                error.addClass('invalid-feedback').removeClass('valid-feedback');
                element.addClass('is-invalid').removeClass('is-valid');
                insertValidation(element, error);
            },
            success(label, element) {
                // Add element to display the success message
                let $element = $(element), $parent = $element.parent();
                if ($parent.find('.valid-feedback').length === 0) {
                    insertValidation($element, $('<div class="valid-feedback"></div>'));
                }
            },
            highlight(element) {
                $(element).addClass('is-invalid').removeClass('is-valid');
            },
            unhighlight(element) {
                let $element = $(element), $parent = $element.parent();

                if ($element.val() !== '') {
                    $element.addClass('is-valid');
                }

                $element.removeClass('is-invalid');
                $parent.find('.invalid-feedback').remove();
            },
        }, options));
    }

    /**
     * Validate specific element 
     * 
     * @param {jQuery|object} element
     * @param {object} options
     * @param {object} ajaxSettings
     * @return {jQuery}
     */
    validate(element, options = {}, ajaxSettings =  {}) {
        options = $.extend({
            inputCheck: null, // {function}
            clientOnly: false, // {boolean}
            responseCallback: null, // {function}
            reset : this._reset, // {function|null}
            beforeValidate : this._beforeValidate, // {function|null}
            afterValidate : this._afterValidate, // {function|null}
            error : this._error, // {function|null}
            notice : this._notice, // {function|null}
            success : this._success // {function|null}
        }, options);

        let $element = $(element);

        if (options.reset) {
            options.reset.call(this, $element);
        }

        if (typeof options.inputCheck === 'function') {
            let result = options.inputCheck.call(this, element);
            if (typeof result === 'object') {
                if (typeof result.success === 'string') {
                    options.success.call(this, $element, result);
                } else if (typeof result.error === 'string') {
                    options.error.call(this, $element, result);
                } else if (typeof result.notice === 'string') {
                    options.notice.call(this, $element, result);
                }
                if (options.clientOnly || result.breakon === true) {
                    return;
                }
            }
        }

        // Skip server side validation request
        if (options.clientOnly) {
            return;
        }
            
        if (options.beforeValidate) {
            options.beforeValidate.call(this, $element);
        }

        let postData = $element.serialize();
        // Checkbox is unchecked, pass value as 0
        if (postData === '' && element.type === 'checkbox') {
            postData = encodeURI(`${element.name}=0`);
        }

        return $.ajax($.extend({
            type: 'POST',
            url: this._url(element),
            dataType: 'json',
            data: postData,
            success: (response) => {
                if (options.afterValidate) {
                    options.afterValidate.call(this, $element);
                }
                if (typeof options.responseCallback === 'function') {
                    response = options.responseCallback.call(this, response);
                }
                if (response.error) {
                    options.error.call(this, $element, response);
                } else if (response.notice) {
                    options.notice.call(this, $element, response);
                } else if (response.success) {
                    options.success.call(this, $element, response);
                }
            }
        }, ajaxSettings));
    }

    getValidator() {
        return this.validator;
    }


    /**
     * Is form valid
     */
    valid() {
        return this.form.valid();
    }

    /**
     * Reset form and validator
     */
    reset() {
        this.validator.resetForm();
        this.form.find('input, textarea, select').removeClass('is-valid').removeClass('is-invalid');
        this.form.trigger('reset');
    }

    /**
     * Remove the 'error' class on the div wrapper w/c is rendered by cakephp form helper if has error.
     * As the jqueryvalidation plugin will hide the input/fields upon form submission
     */
    fixCakeFormInput(selector = '.form-group') {
        $(selector).removeClass('error');
    }

    /**
     * Form the final validation URL
     */
    _url(element) {
        return this.url + '/' + element.name + '/' + (element.id || element.name)
            + '/' + this.validation;
    }
   
    /**
     * The default callback after successful server side validation
     * 
     * @param {jQuery|object} $element
     * @param {object} response
     */
    _success($element, response) {
        let $parent = $element.parent();
        if ($element.val() === '') {
            return;
        }

        if (response.success) {
            // Add element to display the success message if not existing
            if ($parent.find('.valid-feedback').length === 0) {
                this.insertValidation($element, $('<div class="valid-feedback"></div>'));
            }
            $element.addClass('is-valid').removeClass('is-invalid');
            $parent.find('.valid-feedback').html(response.success).show();
        }
    }

    /**
     * The default callback after failed server side validation
     *
     * @param {jQuery|object} $element
     */
    _error($element, response) {
        const error = {};
        error[$element[0].name] = response.error;
        this.validator.showErrors(error);
    }

    /**
     * The default callback after failed server side validation for notice messages
     *
     * @param {jQuery|object} $element
     */
    _notice($element, response) {
        const error = {};
        error[$element[0].name] = response.notice;
        this.validator.showErrors(error);
    }

    /**
     * The default callback before or while server side validation is in progress
     * 
     * @param {jQuery|object} $element
     */
    _beforeValidate($element) {
        let $parent = $element.parent();
        if ($parent.find('.validating').length === 0) {
            this.insertValidation($element, $(`<div class="form-text validating">
                <div class="spinner-border spinner-border-sm" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                Checking...
            </div>`));

        }
        $parent.find('.validating').show();
    }

    /**
     * The default callback after the server side validation
     * 
     * @param {jQuery|object} $element
     */
    _afterValidate($element) {
        $element.parent().find('.validating').hide();
    }

    /**
     * The default callback to reset the element before server side validattion
     * 
     * @param {jQuery|object} $element
     */
    _reset($element) {
        let $parent = $element.parent();
        $parent.find('.valid-feedback, .invalid-feedback, .error-message')
            .html('').hide();
    }

}
